import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Button from "components/Button";
import { BROKER_FR_STATUS_LABELS, CARRIER_FR_STATUS_LABELS } from "constants/base";
import { routes } from "constants/routes";
import { formatNumber } from "helpers/formatNumber";
import { useUserFeature } from "hooks/useUserFeature";
import { ISummaryData } from "models/HaulPay";
import styles from "./style.module.scss";
import { IOption } from "../../../../../models/Form";

type StatusSummaryProps = {
  summaryData: ISummaryData;
  source?: string;
  debtor?: string;
  debtorName?: string;
  frTypeOption?: IOption;
};

export default function StatusSummary({
  summaryData,
  debtor,
  debtorName,
  frTypeOption
}: Readonly<StatusSummaryProps>) {
  const { isBroker } = useUserFeature();
  const navigate = useNavigate();
  const location = useLocation();
  const FR_LABELS = isBroker ? BROKER_FR_STATUS_LABELS : CARRIER_FR_STATUS_LABELS;

  const sortedSummary = useMemo(() => {
    const newSummary: ISummaryData = {};
    Object.keys(FR_LABELS).forEach((key) => {
      if (summaryData[key] && (summaryData[key].count > 0 || key === "all")) {
        newSummary[key] = summaryData[key];
      }
    });
    return newSummary;
  }, [summaryData]);

  const navigateToStatusPage = (status: string) => {
    navigate(`${location.pathname}/${status}`, { state: { debtor, debtorName, frTypeOption } });
  };

  return Object.keys(sortedSummary).length !== 0 ? (
    <div className={styles.summaryContainer}>
      <div className={styles.statusSummaryWrapper}>
        {Object.entries(sortedSummary).map(([status, data]) => {
          const title = FR_LABELS[status];
          return (
            <button
              key={title}
              className={styles.statusSummaryCard}
              onClick={() => navigateToStatusPage(status)}>
              <span className={styles.title}>{title}</span>
              <ul className={styles.content}>
                <li className="col-span-1">
                  <span className={styles.subtitle}>Count</span>
                  <span>{data.count ?? 0}</span>
                </li>
                <li className="col-span-2">
                  <span className={styles.subtitle}>Total Amount</span>
                  <span>{formatNumber(data.total_amount / 100, 0)}</span>
                </li>
              </ul>
            </button>
          );
        })}
      </div>

      {isBroker && (
        <Button
          type="button"
          variant="outline"
          className={clsx(styles.createLoadButton, "!bg-white !h-12")}
          rounded
          onClick={() => navigate(routes.HAULPAY_BROKER_FUNDING_REQUEST)}>
          <Icon path={mdiPlus} className="size-7" />
          <span>Create Funding Request</span>
        </Button>
      )}
    </div>
  ) : (
    <div />
  );
}
